export interface Disaster {
  id: string;
  type: 'earthquake' | 'wildfire' | 'flood' | 'hurricane' | 'tornado';
  location: string;
  coordinates: [number, number];
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'active' | 'monitoring' | 'resolved';
  affected: number;
  timestamp: Date;
  description: string;
}

// Base interface for all geographic threats
export type ThreatCategory = 
  | 'natural_disaster'
  | 'human_health'
  | 'animal_health'
  | 'plant_agriculture'
  | 'environmental'
  | 'war_conflict'
  | 'technological'
  | 'cosmic';

export interface GeoThreat {
  id: string;
  type: string;
  location: string;
  coordinates: [number, number];
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'active' | 'monitoring' | 'resolved';
  impactedCount: number; // Unified field for affected population/entities
  timestamp: Date;
  description: string;
  category: ThreatCategory;
}

// Human Health Threats
export interface HumanHealthThreat extends GeoThreat {
  category: 'human_health';
  type: 'infectious_disease' | 'non_communicable_disease' | 'environmental_health' | 'nutritional' | 'healthcare_access';
  affectedPopulation: number;
}

// War and Conflict Threats
export interface WarConflictThreat extends GeoThreat {
  category: 'war_conflict';
  type: 'international_war' | 'civil_war' | 'terrorism' | 'war_crimes' | 'refugee_crisis' | 'post_conflict';
  displacedPeople: number;
}

// Animal Health Threats
export interface AnimalHealthThreat extends GeoThreat {
  category: 'animal_health';
  type: 'wildlife_disease' | 'livestock_health' | 'endangered_species' | 'habitat_stress';
  affectedAnimals: number;
}

// Plant and Agricultural Threats
export interface PlantAgricultureThreat extends GeoThreat {
  category: 'plant_agriculture';
  type: 'crop_disease' | 'invasive_species' | 'soil_degradation' | 'pollinator_decline' | 'forest_health';
  affectedArea: number; // in hectares
}

// Environmental Threats
export interface EnvironmentalThreat extends GeoThreat {
  category: 'environmental';
  type: 'climate_change' | 'pollution' | 'habitat_destruction' | 'biodiversity_loss';
  affectedEcosystem: number; // area in km²
}

// Technological Threats
export interface TechnologicalThreat extends GeoThreat {
  category: 'technological';
  type: 'cybersecurity' | 'ai_risks' | 'nuclear_threats' | 'genetic_engineering';
  affectedSystems: number;
}

export interface CommunityPost {
  id: string;
  author: {
    name: string;
    avatar: string;
    verified: boolean;
  };
  content: string;
  images?: string[];
  location: string;
  timestamp: Date;
  likes: number;
  comments: number;
  tags: string[];
  emergency: boolean;
}

export interface SafetyProtocol {
  id: string;
  type: string;
  title: string;
  description: string;
  steps: string[];
  priority: 'high' | 'medium' | 'low';
  estimatedTime: string;
}

// Convert existing disasters to GeoThreat format
export const mockDisasters: GeoThreat[] = [
  {
    id: '1',
    type: 'wildfire',
    location: 'California Central Valley',
    coordinates: [-119.7871, 36.7378],
    severity: 'critical',
    status: 'active',
    impactedCount: 15000,
    timestamp: new Date('2025-01-20T08:30:00'),
    description: 'Large wildfire spreading rapidly due to strong winds and dry conditions.',
    category: 'natural_disaster',
  },
  {
    id: '2',
    type: 'earthquake',
    location: 'Tokyo Bay, Japan',
    coordinates: [139.6917, 35.6895],
    severity: 'high',
    status: 'monitoring',
    impactedCount: 250000,
    timestamp: new Date('2025-01-20T14:15:00'),
    description: '6.2 magnitude earthquake with potential for aftershocks.',
    category: 'natural_disaster',
  },
  {
    id: '3',
    type: 'flood',
    location: 'Bangladesh Delta',
    coordinates: [90.3563, 23.6850],
    severity: 'high',
    status: 'active',
    impactedCount: 45000,
    timestamp: new Date('2025-01-19T22:45:00'),
    description: 'Monsoon floods affecting multiple districts.',
    category: 'natural_disaster',
  },
  {
    id: '4',
    type: 'hurricane',
    location: 'Gulf of Mexico',
    coordinates: [-89.6816, 29.9511],
    severity: 'medium',
    status: 'monitoring',
    impactedCount: 85000,
    timestamp: new Date('2025-01-20T06:00:00'),
    description: 'Category 2 hurricane approaching the coast.',
    category: 'natural_disaster',
  },
  {
    id: '5',
    type: 'tornado',
    location: 'Oklahoma Plains',
    coordinates: [-97.5348, 35.4676],
    severity: 'low',
    status: 'resolved',
    impactedCount: 1200,
    timestamp: new Date('2025-01-18T16:20:00'),
    description: 'EF2 tornado caused property damage, no casualties reported.',
    category: 'natural_disaster',
  },
];

export const mockHumanHealthThreats: HumanHealthThreat[] = [
  {
    id: 'h1',
    type: 'infectious_disease',
    location: 'Central Africa',
    coordinates: [15.2551, -4.0383],
    severity: 'critical',
    status: 'active',
    impactedCount: 125000,
    affectedPopulation: 125000,
    timestamp: new Date('2025-01-15T10:00:00'),
    description: 'Ebola outbreak spreading across multiple regions with limited healthcare infrastructure.',
    category: 'human_health',
  },
  {
    id: 'h2',
    type: 'environmental_health',
    location: 'New Delhi, India',
    coordinates: [77.1025, 28.7041],
    severity: 'high',
    status: 'active',
    impactedCount: 2000000,
    affectedPopulation: 2000000,
    timestamp: new Date('2025-01-18T08:00:00'),
    description: 'Severe air pollution reaching hazardous levels, causing respiratory health crisis.',
    category: 'human_health',
  },
  {
    id: 'h3',
    type: 'nutritional',
    location: 'Horn of Africa',
    coordinates: [43.3183, 11.8251],
    severity: 'critical',
    status: 'active',
    impactedCount: 850000,
    affectedPopulation: 850000,
    timestamp: new Date('2025-01-10T12:00:00'),
    description: 'Severe malnutrition crisis affecting children and vulnerable populations due to drought.',
    category: 'human_health',
  },
  {
    id: 'h4',
    type: 'healthcare_access',
    location: 'Rural Appalachia, USA',
    coordinates: [-82.5515, 37.5407],
    severity: 'medium',
    status: 'monitoring',
    impactedCount: 45000,
    affectedPopulation: 45000,
    timestamp: new Date('2025-01-12T14:30:00'),
    description: 'Medical desert with limited access to healthcare services and specialists.',
    category: 'human_health',
  },
];

export const mockWarConflictThreats: WarConflictThreat[] = [
  {
    id: 'w1',
    type: 'civil_war',
    location: 'Eastern Ukraine',
    coordinates: [37.9755, 48.3794],
    severity: 'critical',
    status: 'active',
    impactedCount: 1500000,
    displacedPeople: 1500000,
    timestamp: new Date('2025-01-19T16:00:00'),
    description: 'Ongoing conflict causing massive displacement and humanitarian crisis.',
    category: 'war_conflict',
  },
  {
    id: 'w2',
    type: 'refugee_crisis',
    location: 'Syrian Border, Turkey',
    coordinates: [36.2012, 36.1612],
    severity: 'high',
    status: 'active',
    impactedCount: 750000,
    displacedPeople: 750000,
    timestamp: new Date('2025-01-16T09:30:00'),
    description: 'Large refugee population requiring humanitarian assistance and resettlement.',
    category: 'war_conflict',
  },
  {
    id: 'w3',
    type: 'terrorism',
    location: 'Sahel Region, Mali',
    coordinates: [-3.9962, 17.5707],
    severity: 'high',
    status: 'active',
    impactedCount: 250000,
    displacedPeople: 250000,
    timestamp: new Date('2025-01-17T11:15:00'),
    description: 'Terrorist activities forcing population displacement and disrupting local communities.',
    category: 'war_conflict',
  },
  {
    id: 'w4',
    type: 'post_conflict',
    location: 'South Sudan',
    coordinates: [31.3069, 6.8770],
    severity: 'medium',
    status: 'monitoring',
    impactedCount: 180000,
    displacedPeople: 180000,
    timestamp: new Date('2025-01-14T13:45:00'),
    description: 'Post-conflict reconstruction challenges with ongoing displacement and trauma recovery needs.',
    category: 'war_conflict',
  },
];

export const mockAnimalHealthThreats: AnimalHealthThreat[] = [
  {
    id: 'a1',
    type: 'wildlife_disease',
    location: 'Amazon Rainforest, Brazil',
    coordinates: [-60.0261, -3.4653],
    severity: 'high',
    status: 'active',
    impactedCount: 50000,
    affectedAnimals: 50000,
    timestamp: new Date('2025-01-16T07:20:00'),
    description: 'Fungal disease outbreak affecting multiple primate species with potential zoonotic transmission.',
    category: 'animal_health',
  },
  {
    id: 'a2',
    type: 'endangered_species',
    location: 'Sumatra, Indonesia',
    coordinates: [101.3431, -0.7893],
    severity: 'critical',
    status: 'active',
    impactedCount: 800,
    affectedAnimals: 800,
    timestamp: new Date('2025-01-12T15:10:00'),
    description: 'Critically endangered Sumatran orangutan population declining due to habitat loss.',
    category: 'animal_health',
  },
];

export const mockPlantAgricultureThreats: PlantAgricultureThreat[] = [
  {
    id: 'p1',
    type: 'crop_disease',
    location: 'Midwest USA',
    coordinates: [-93.2650, 41.5868],
    severity: 'high',
    status: 'active',
    impactedCount: 500000,
    affectedArea: 500000,
    timestamp: new Date('2025-01-18T09:45:00'),
    description: 'Corn blight affecting major agricultural regions, threatening food security.',
    category: 'plant_agriculture',
  },
  {
    id: 'p2',
    type: 'pollinator_decline',
    location: 'European Union',
    coordinates: [10.4515, 51.1657],
    severity: 'medium',
    status: 'monitoring',
    impactedCount: 200000,
    affectedArea: 200000,
    timestamp: new Date('2025-01-15T11:30:00'),
    description: 'Significant decline in bee populations affecting agricultural pollination.',
    category: 'plant_agriculture',
  },
];

export const mockEnvironmentalThreats: EnvironmentalThreat[] = [
  {
    id: 'e1',
    type: 'pollution',
    location: 'Great Pacific Garbage Patch',
    coordinates: [-145.0000, 35.0000],
    severity: 'critical',
    status: 'active',
    impactedCount: 1000000,
    affectedEcosystem: 1000000,
    timestamp: new Date('2025-01-10T08:00:00'),
    description: 'Massive plastic pollution affecting marine ecosystems and food chains.',
    category: 'environmental',
  },
  {
    id: 'e2',
    type: 'habitat_destruction',
    location: 'Borneo, Malaysia',
    coordinates: [114.7277, 2.2180],
    severity: 'high',
    status: 'active',
    impactedCount: 75000,
    affectedEcosystem: 75000,
    timestamp: new Date('2025-01-14T12:15:00'),
    description: 'Rapid deforestation threatening biodiversity and indigenous communities.',
    category: 'environmental',
  },
];

export const mockTechnologicalThreats: TechnologicalThreat[] = [
  {
    id: 't1',
    type: 'cybersecurity',
    location: 'Global Infrastructure',
    coordinates: [0, 0],
    severity: 'high',
    status: 'active',
    impactedCount: 10000000,
    affectedSystems: 10000000,
    timestamp: new Date('2025-01-19T14:20:00'),
    description: 'Large-scale cyberattack targeting critical infrastructure and financial systems.',
    category: 'technological',
  },
  {
    id: 't2',
    type: 'nuclear_threats',
    location: 'Chernobyl Exclusion Zone',
    coordinates: [30.0619, 51.3890],
    severity: 'medium',
    status: 'monitoring',
    impactedCount: 5000,
    affectedSystems: 5000,
    timestamp: new Date('2025-01-17T10:30:00'),
    description: 'Elevated radiation levels detected, monitoring for potential containment issues.',
    category: 'technological',
  },
];

export const mockCommunityPosts: CommunityPost[] = [
  {
    id: '1',
    author: {
      name: 'Sarah Chen',
      avatar: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?w=150',
      verified: true,
    },
    content: 'Setting up emergency shelter at community center. We have space for 50 more families. Hot meals available every 2 hours. Please share with those in need. #CaliforniaFires #CommunitySupport',
    location: 'Fresno, CA',
    timestamp: new Date('2025-01-20T10:30:00'),
    likes: 127,
    comments: 23,
    tags: ['shelter', 'food', 'emergency'],
    emergency: true,
  },
  {
    id: '2',
    author: {
      name: 'Mike Rodriguez',
      avatar: 'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?w=150',
      verified: false,
    },
    content: 'Does anyone have extra water bottles? Our local distribution center is running low. Can pick up from anywhere in the downtown area.',
    location: 'Sacramento, CA',
    timestamp: new Date('2025-01-20T09:15:00'),
    likes: 45,
    comments: 12,
    tags: ['supplies', 'water'],
    emergency: false,
  },
  {
    id: '3',
    author: {
      name: 'Emily Johnson',
      avatar: 'https://images.pexels.com/photos/1130626/pexels-photo-1130626.jpeg?w=150',
      verified: true,
    },
    content: 'Medical team deployed to affected areas. If you need medical assistance and cannot reach hospitals, contact us directly. We have mobile units operational 24/7.',
    images: ['https://images.pexels.com/photos/263402/pexels-photo-263402.jpeg?w=400'],
    location: 'Los Angeles, CA',
    timestamp: new Date('2025-01-19T20:45:00'),
    likes: 203,
    comments: 34,
    tags: ['medical', 'emergency', 'support'],
    emergency: true,
  },
];

export const mockSafetyProtocols: SafetyProtocol[] = [
  {
    id: '1',
    type: 'earthquake',
    title: 'Earthquake Response Protocol',
    description: 'Essential steps to take during and after an earthquake to ensure safety.',
    priority: 'high',
    estimatedTime: '2-5 minutes',
    steps: [
      'Drop to hands and knees immediately',
      'Take cover under a desk or table if available',
      'Hold on to your shelter and protect your head',
      'Stay where you are until shaking stops',
      'Exit building only if safe to do so',
      'Stay away from damaged structures',
      'Check for injuries and provide first aid',
      'Listen for emergency broadcasts',
    ],
  },
  {
    id: '2',
    type: 'wildfire',
    title: 'Wildfire Evacuation Protocol',
    description: 'Critical actions for wildfire evacuation and safety.',
    priority: 'high',
    estimatedTime: '10-30 minutes',
    steps: [
      'Monitor emergency alerts and evacuation orders',
      'Prepare go-bag with essentials',
      'Close all windows and doors',
      'Turn off gas utilities',
      'Follow designated evacuation routes',
      'Do not return until authorities give all-clear',
      'Stay informed through official channels',
    ],
  },
  {
    id: '3',
    type: 'flood',
    title: 'Flood Safety Protocol',
    description: 'Safety measures for flood situations and water emergencies.',
    priority: 'high',
    estimatedTime: '5-15 minutes',
    steps: [
      'Move to higher ground immediately',
      'Avoid walking or driving through flood water',
      'Turn off electricity if safe to do so',
      'Do not drink flood water',
      'Signal for help if trapped',
      'Stay away from downed power lines',
      'Wait for rescue if necessary',
    ],
  },
];